import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { useGlobalCopyStore } from '../../common'
import { KeyIndicatorsTabItemType } from '../constants'

import CreditScoreContent from './CreditScore/CreditScoreContent'
import CreditScoreHeader from './CreditScore/CreditScoreHeader'
import KeyIndicatorsDrawer from './KeyIndicatorsDrawer'
import MaintainingEmergencySavingsContent from './MaintainingEmergencySavings/MaintainingEmergencySavingsContent'
import MaintainingEmergencySavingsSticky from './MaintainingEmergencySavings/MaintainingEmergencySavingsSticky'
import PayBillsOnTimeDrawerComponent from './paybillsontime/PayBillsOnTimeDrawerComponent'
import PayBillsOnTimeStickyComponent from './paybillsontime/PayBillsOnTimeStickyComponent'
import SaveEnoughToLiveOnDrawerContent from './SaveEnoughToLiveOn/SaveEnoughToLiveOnDrawerContent'
import SaveEnoughToLiveOnProgressBar from './SaveEnoughToLiveOn/SaveEnoughToLiveOnProgressBar'
import HaveManageableDebtProgressBar from './HaveManageableDebt/HaveManageableDebtProgressBar'
import HaveManageableDebtComponent from './HaveManageableDebt/HaveManageableDebtComponent'
import SpendLessThanYouMakeProgressBar from './SpendLessThanYouMake/SpendLessThanYouMakeProgressBar'
import SpendLessThanYouMakeComponent from './SpendLessThanYouMake/SpendLessThanYouMakeComponent'

interface KeyIndicatorsTabItemDrawerProps {
  drawerFor: KeyIndicatorsTabItemType
  isOpen: boolean
  onClose: () => void
}

const KeyIndicatorsTabItemDrawer = ({
  drawerFor,
  isOpen,
  onClose,
}: KeyIndicatorsTabItemDrawerProps) => {
  const { finstrong: copy } = useGlobalCopyStore()

  //TODO : When the Drawer for key indicators other than Save Enough To Live On are implemented,
  // add their respective content, titles, descriptions, and sticky components here.
  const contentMapping: Record<
    KeyIndicatorsTabItemType,
    {
      connectTitle?: string
      connectDescription?: string
      title: React.ReactNode
      description: string
      stickyComponent: React.ReactNode
      content: React.ReactNode
      drawerType?: 'connectDrawer' | 'connectionsDrawer'
    }
  > = {
    spendLessThanYouMake: {
      title:
        copy.key_indicators_spend.spend_less_than_you_make_drawer.spend_less_than_you_make_title,
      description:
        copy.key_indicators_spend.spend_less_than_you_make_drawer
          .spend_less_than_you_make_description,
      stickyComponent: <SpendLessThanYouMakeProgressBar />,
      content: <SpendLessThanYouMakeComponent />,
      drawerType: 'connectDrawer',
      connectTitle: copy.connect_your_accounts,
      connectDescription:
        copy.connect_more_accounts_to_unlock_a_fuller_understanding_of_your_finances,
    },
    payBillsOnTime: {
      title: copy.key_indicators_spend.pay_bills_on_time_drawer.pay_bills_on_time_title,
      description: copy.key_indicators_spend.pay_bills_on_time_drawer.pay_bills_on_time_description,
      stickyComponent: <PayBillsOnTimeStickyComponent />,
      content: <PayBillsOnTimeDrawerComponent />,
      drawerType: 'connectDrawer',
      connectTitle: copy.connect_your_accounts,
      connectDescription:
        copy.connect_more_accounts_to_unlock_a_fuller_understanding_of_your_finances,
    },
    maintainEmergencySavings: {
      content: <MaintainingEmergencySavingsContent />,
      description: copy.maintain_emergency_savings_description,
      stickyComponent: <MaintainingEmergencySavingsSticky />,
      title: copy.maintain_emergency_savings_title,
      drawerType: 'connectDrawer',
      connectTitle: copy.connect_your_accounts,
      connectDescription:
        copy.connect_more_accounts_to_unlock_a_fuller_understanding_of_your_finances,
    },
    haveManageableDebt: {
      title: copy.key_indicators_borrow.have_manageable_debt_drawer.have_manageable_debt_title,
      description:
        copy.key_indicators_borrow.have_manageable_debt_drawer.have_manageable_debt_description,
      stickyComponent: <HaveManageableDebtProgressBar />,
      content: <HaveManageableDebtComponent />,
      drawerType: 'connectDrawer',
      connectTitle: copy.dont_see_all_your_accounts,
      connectDescription: copy.connect_more_accounts_to_view_more_accurate_financial_picture,
    },
    haveAGoodCreditScore: {
      title: <CreditScoreHeader />,
      description: copy.credit_score_description,
      stickyComponent: null,
      content: <CreditScoreContent />,
    },
    saveEnoughToLiveOn: {
      title: copy.key_indicators_save.drawer_title,
      description: copy.key_indicators_save.drawer_description,
      stickyComponent: <SaveEnoughToLiveOnProgressBar />,
      content: <SaveEnoughToLiveOnDrawerContent />,
      drawerType: 'connectDrawer',
      connectTitle: copy.dont_see_all_your_accounts,
      connectDescription: copy.connect_more_accounts_to_view_more_accurate_financial_picture,
    },
  }

  const content = contentMapping[drawerFor]
  return (
    <KeyIndicatorsDrawer
      connectDescription={content?.connectDescription}
      connectTitle={content?.connectTitle}
      description={content?.description}
      drawerTitle={copy.key_indicators}
      drawerType={content.drawerType}
      isOpen={isOpen}
      loadConnectAccountsCard={true}
      onClose={onClose}
      stickyComponent={content.stickyComponent}
      title={content.title}
    >
      <Stack sx={{ gap: 16 }}>{content.content}</Stack>
    </KeyIndicatorsDrawer>
  )
}

export default observer(KeyIndicatorsTabItemDrawer)
